import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { DollarSign, Calendar, CheckCircle, XCircle, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentsView = ({ auth }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPayments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/payments`, {
        auth: { username: auth.username, password: auth.password }
      });
      setPayments(response.data?.items || response.data || []);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [auth.username, auth.password]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const getStatusIcon = (status) => {
    switch (status) {
      case "paid":
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case "pending":
        return <Clock className="w-5 h-5 text-yellow-400" />;
      default:
        return <XCircle className="w-5 h-5 text-red-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "paid":
        return "Pagado";
      case "pending":
        return "Pendiente";
      default:
        return "Cancelado";
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="payments-view"
    >
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Transacciones</h1>
        <p className="text-[#94a3b8]">Historial de pagos y transacciones</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#3b82f6] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="glass-card overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#0b1221]">
              <tr>
                <th className="text-left p-4 text-[#94a3b8] font-medium">Plan</th>
                <th className="text-left p-4 text-[#94a3b8] font-medium">Email</th>
                <th className="text-left p-4 text-[#94a3b8] font-medium">Monto</th>
                <th className="text-left p-4 text-[#94a3b8] font-medium">Estado</th>
                <th className="text-left p-4 text-[#94a3b8] font-medium">Fecha</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr key={payment.id} className="border-t border-white/5">
                  <td className="p-4 text-white">{payment.package_name}</td>
                  <td className="p-4 text-[#94a3b8]">{payment.email || "-"}</td>
                  <td className="p-4">
                    <span className="flex items-center gap-1 text-white">
                      <DollarSign className="w-4 h-4 text-green-400" />
                      {payment.amount} USD
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="flex items-center gap-2">
                      {getStatusIcon(payment.payment_status)}
                      <span className={
                        payment.payment_status === "paid" ? "text-green-400" :
                        payment.payment_status === "pending" ? "text-yellow-400" : "text-red-400"
                      }>
                        {getStatusText(payment.payment_status)}
                      </span>
                    </span>
                  </td>
                  <td className="p-4 text-[#94a3b8]">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {new Date(payment.created_at).toLocaleDateString('es-ES')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {payments.length === 0 && (
            <div className="text-center py-20 text-[#94a3b8]">
              No hay transacciones registradas.
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default PaymentsView;
